import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import addIcon from "@iconify/icons-solar/user-plus-linear";
import backIcon from "@iconify/icons-solar/arrow-left-linear";
import editIcon from "@iconify/icons-solar/pen-linear";
import arrowIcon from "@iconify/icons-solar/arrow-right-linear";
import TopBar from "../components/layout/TopBar";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirmation } from "../context/ConfirmationContext";
import { api } from "../lib/api";
import "./Agents.css";

type AgentRole = "AGENT" | "MANAGER" | "BROKER";
interface AgentSale {
  id: string;
  stage: string;
  saleAmount: number;
  recordedReceipts: number;
  commission: number;
  createdAt: string;
  lotNumber: string;
  clientName: string;
  needsReview: boolean;
}
interface Agent {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: AgentRole;
  createdAt: string;
  updatedAt: string;
  assignedClients: number;
  metrics: {
    deals: number;
    openDeals: number;
    completedDeals: number;
    saleValue: number;
    recordedReceipts: number;
    commission: number;
    accountsToReview: number;
  };
  sales: AgentSale[];
}
const roles: Record<AgentRole, string> = {
  AGENT: "Agent",
  MANAGER: "Manager",
  BROKER: "Broker",
};
const stages: Record<string, string> = {
  INQUIRY: "Inquiry",
  RESERVATION: "Reservation",
  CONTRACT_SIGNING: "Contract",
  PAYMENT: "Payment",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};
const money = (n: number) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    maximumFractionDigits: 2,
  }).format(n);
const date = (s: string) =>
  new Date(s).toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "Asia/Manila",
  });
const initials = (s: string) =>
  s
    .trim()
    .split(/\s+/)
    .map((v) => v[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
const blank = () => ({
  name: "",
  email: "",
  phone: "",
  role: "AGENT" as AgentRole,
  password: "",
});
export default function Agents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { confirmAction } = useConfirmation();
  const admin = user?.role === "ADMIN";
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState(""),
    [refresh, setRefresh] = useState(0);
  const [search, setSearch] = useState(""),
    [role, setRole] = useState("ALL"),
    [workload, setWorkload] = useState("ALL"),
    [sort, setSort] = useState("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false),
    [editing, setEditing] = useState<Agent | null>(null),
    [form, setForm] = useState(blank),
    [dirty, setDirty] = useState(false),
    [busy, setBusy] = useState(false),
    [formError, setFormError] = useState("");
  const saving = useRef(false);
  const formHeading = useRef<HTMLHeadingElement>(null);
  const pageHeading = useRef<HTMLHeadingElement>(null);
  const firstView = useRef(true);
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    if (!formOpen) pageHeading.current?.focus();
  }, [selectedId, formOpen]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    api.agents
      .list()
      .then((data) => {
        if (!cancelled) setAgents(data.agents);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);
  useEffect(() => {
    if (formOpen) formHeading.current?.focus();
  }, [formOpen]);
  useEffect(() => {
    const leave = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", leave);
    return () => window.removeEventListener("beforeunload", leave);
  }, [dirty]);
  const selected = agents.find((a) => a.id === selectedId);
  const filtered = useMemo(
    () =>
      agents
        .filter(
          (a) =>
            `${a.name} ${a.email} ${a.phone || ""}`
              .toLowerCase()
              .includes(search.trim().toLowerCase()) &&
            (role === "ALL" || role === a.role) &&
            (workload === "ALL" ||
              (workload === "OPEN"
                ? a.metrics.openDeals > 0
                : a.metrics.openDeals === 0)),
        )
        .sort((a, b) =>
          sort === "name"
            ? a.name.localeCompare(b.name)
            : b.metrics[sort as "openDeals" | "saleValue" | "commission"] -
                a.metrics[sort as "openDeals" | "saleValue" | "commission"] ||
              a.name.localeCompare(b.name),
        ),
    [agents, search, role, workload, sort],
  );
  const totals = agents.reduce(
    (t, a) => ({
      open: t.open + a.metrics.openDeals,
      value: t.value + Math.round(a.metrics.saleValue * 100),
      commission: t.commission + Math.round(a.metrics.commission * 100),
    }),
    { open: 0, value: 0, commission: 0 },
  );
  const openForm = (a?: Agent) => {
    setEditing(a || null);
    setForm(
      a
        ? {
            name: a.name,
            email: a.email,
            phone: a.phone || "",
            role: a.role,
            password: "",
          }
        : blank(),
    );
    setFormError("");
    setDirty(false);
    setFormOpen(true);
  };
  const closeForm = async () => {
    if (saving.current) return;
    if (
      dirty &&
      !(await confirmAction({
        title: "Discard unsaved agent details?",
        description: "These changes have not been saved.",
        confirmLabel: "Discard changes",
        action: () => {},
      }))
    )
      return;
    setFormOpen(false);
    setForm(blank());
    setDirty(false);
  };
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving.current) return;
    if (!editing && new TextEncoder().encode(form.password).length > 72) {
      setFormError(
        "Use a password of at most 72 bytes. Non-English characters may use more than one byte.",
      );
      return;
    }
    saving.current = true;
    setBusy(true);
    setFormError("");
    try {
      if (editing)
        await api.agents.update(editing.id, {
          name: form.name,
          phone: form.phone,
          expectedUpdatedAt: editing.updatedAt,
        });
      else await api.auth.register(form);
      setFormOpen(false);
      setDirty(false);
      setForm(blank());
      setRefresh((n) => n + 1);
      addToast(
        "success",
        editing ? "Agent details saved" : "Agent account created",
        editing
          ? "The directory now uses the updated details."
          : "The agent can sign in to VOP CRM with the account details you entered.",
      );
    } catch (e: any) {
      setFormError(e.message);
      addToast("error", "Agent could not be saved", e.message);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };
  const change = (key: keyof ReturnType<typeof blank>, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };
  const resetFilters = () => {
    setSearch("");
    setRole("ALL");
    setWorkload("ALL");
  };
  return (
    <>
      <TopBar title="Agents" showSearch={false} />
      {admin && <div style={{padding:"16px 32px 0"}}><button className="ag-button" onClick={()=>navigate("/settings/users")}>User management · Production responsibilities</button></div>}
      <main className="agents-workspace">
        <header className="agents-heading">
          <div>
            <h2 ref={pageHeading} tabIndex={-1}>
              {formOpen
                ? editing
                  ? "Edit agent details"
                  : "Add an agent"
                : selected
                  ? selected.name
                  : "Agent directory"}
            </h2>
            <p>
              {formOpen
                ? editing
                  ? "Keep contact details current for your sales team."
                  : "Create a VOP CRM account for a new member of your sales team."
                : selected
                  ? "Contact details and recorded sales performance."
                  : "Manage your sales team and review their assigned deals."}
            </p>
          </div>
          {!formOpen &&
            (selected
              ? admin && (
                  <button
                    className="ag-button"
                    onClick={() => openForm(selected)}
                  >
                    <Icon icon={editIcon} width={18} />
                    Edit details
                  </button>
                )
              : admin && (
                  <button
                    className="ag-button primary"
                    onClick={() => openForm()}
                  >
                    <Icon icon={addIcon} width={18} />
                    Add agent
                  </button>
                ))}
        </header>
        {formOpen && admin ? (
          <form className="ag-form" onSubmit={save}>
            <h3 tabIndex={-1} ref={formHeading}>
              {editing ? "Contact information" : "Account details"}
            </h3>
            {formError && (
              <p className="ag-error" role="alert">
                {formError}
              </p>
            )}
            <fieldset disabled={busy}>
              <label>
                Full name
                <input
                  autoComplete="name"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => change("name", e.target.value)}
                />
              </label>
              <div className="ag-form-grid">
                <label>
                  Email
                  <input
                    type="email"
                    autoComplete="off"
                    required
                    disabled={!!editing}
                    maxLength={254}
                    value={form.email}
                    onChange={(e) => change("email", e.target.value)}
                  />
                </label>
                <label>
                  Phone <span className="ag-optional">optional</span>
                  <input
                    type="tel"
                    autoComplete="tel"
                    maxLength={40}
                    value={form.phone}
                    onChange={(e) => change("phone", e.target.value)}
                  />
                </label>
              </div>
              <label>
                Access role
                <select
                  value={form.role}
                  disabled={!!editing}
                  onChange={(e) => change("role", e.target.value)}
                >
                  {Object.entries(roles).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {editing ? (
                <p className="ag-help">
                  Sign-in email and access role are shown for reference. This
                  form updates the agent’s name and phone number.
                </p>
              ) : (
                <>
                  <label>
                    Password
                    <input
                      type="password"
                      autoComplete="new-password"
                      required
                      minLength={8}
                      maxLength={72}
                      value={form.password}
                      onChange={(e) => change("password", e.target.value)}
                      aria-describedby="agent-password-help"
                    />
                  </label>
                  <p id="agent-password-help" className="ag-help">
                    At least 8 characters. This account grants access to VOP
                    CRM. No invitation email is sent.
                  </p>
                </>
              )}
            </fieldset>
            <footer>
              <button
                type="button"
                className="ag-button"
                disabled={busy}
                onClick={closeForm}
              >
                Cancel
              </button>
              <button className="ag-button primary" disabled={busy}>
                {busy
                  ? "Saving…"
                  : editing
                    ? "Save details"
                    : "Create agent account"}
              </button>
            </footer>
          </form>
        ) : loadError ? (
          <section className="ag-empty" role="alert">
            <h3>Agent directory unavailable</h3>
            <p>{loadError}</p>
            <button
              className="ag-button"
              onClick={() => setRefresh((n) => n + 1)}
            >
              Retry
            </button>
          </section>
        ) : loading ? (
          <div className="ag-loading" role="status">
            <span>Loading agent records…</span>
            <div />
            <div />
            <div />
          </div>
        ) : selected ? (
          <>
            <button className="ag-back" onClick={() => setSelectedId(null)}>
              <Icon icon={backIcon} width={18} />
              Back to directory
            </button>
            <section className="ag-profile" aria-label="Agent profile">
              <div className="ag-person">
                <span className="ag-avatar" aria-hidden="true">
                  {initials(selected.name)}
                </span>
                <div>
                  <h3>{selected.name}</h3>
                  <span className="ag-role">{roles[selected.role]}</span>
                </div>
              </div>
              <dl>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <a href={`mailto:${selected.email}`}>{selected.email}</a>
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>
                    {selected.phone ? (
                      <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                    ) : (
                      "Not provided"
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Account created</dt>
                  <dd>{date(selected.createdAt)}</dd>
                </div>
                <div>
                  <dt>Assigned clients</dt>
                  <dd>{selected.assignedClients}</dd>
                </div>
              </dl>
            </section>
            <dl className="ag-summary" aria-label="Agent performance">
              <div>
                <dt>Open deals</dt>
                <dd>{selected.metrics.openDeals}</dd>
              </div>
              <div>
                <dt>Completed deals</dt>
                <dd>{selected.metrics.completedDeals}</dd>
              </div>
              <div>
                <dt>Sale value</dt>
                <dd>{money(selected.metrics.saleValue)}</dd>
              </div>
              <div>
                <dt>Recorded commission</dt>
                <dd>{money(selected.metrics.commission)}</dd>
              </div>
            </dl>
            <div className="ag-section-heading">
              <div>
                <h3>Assigned sales</h3>
                <p>
                  Sale value and commission totals exclude cancelled deals.
                  Commission is the amount recorded on the sale.
                </p>
              </div>
              <span>{selected.sales.length} records</span>
            </div>
            {selected.metrics.accountsToReview > 0 && (
              <div className="ag-review">
                <strong>
                  {selected.metrics.accountsToReview}{" "}
                  {selected.metrics.accountsToReview === 1
                    ? "account needs"
                    : "accounts need"}{" "}
                  reconciliation.
                </strong>
                <span>
                  Some historical receipt totals conflict with their payment
                  schedules.
                </span>
                <button
                  className="ag-link"
                  onClick={() => navigate("/payments")}
                >
                  Review in Payments <Icon icon={arrowIcon} width={16} />
                </button>
              </div>
            )}
            {selected.sales.length ? (
              <div className="ag-table-wrap">
                <table className="ag-table ag-sales">
                  <thead>
                    <tr>
                      <th>Lot / client</th>
                      <th>Stage</th>
                      <th className="number">Sale amount</th>
                      <th className="number">Recorded receipts</th>
                      <th className="number">Commission</th>
                      <th>
                        <span className="ag-sr">Open sale</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.sales.map((s) => (
                      <tr key={s.id}>
                        <td data-label="Lot / client">
                          <strong>Lot {s.lotNumber}</strong>
                          <span>{s.clientName}</span>
                          <small>{date(s.createdAt)}</small>
                        </td>
                        <td data-label="Stage">
                          <span
                            className={`ag-stage ${s.stage === "COMPLETED" ? "completed" : ""}`}
                          >
                            {stages[s.stage] || s.stage}
                          </span>
                          {s.stage === "CANCELLED" && (
                            <small>Excluded from totals</small>
                          )}
                        </td>
                        <td data-label="Sale amount" className="number">
                          {money(s.saleAmount)}
                        </td>
                        <td data-label="Recorded receipts" className="number">
                          {money(s.recordedReceipts)}
                          {s.needsReview && (
                            <small className="ag-warning">Needs review</small>
                          )}
                        </td>
                        <td data-label="Commission" className="number">
                          {money(s.commission)}
                        </td>
                        <td className="ag-actions">
                          <button
                            className="ag-link"
                            onClick={() =>
                              navigate("/sales", { state: { saleId: s.id } })
                            }
                          >
                            Open sale
                            <Icon icon={arrowIcon} width={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <section className="ag-empty">
                <h3>No assigned sales yet</h3>
                <p>
                  Sales appear here when this agent is assigned to a deal in the
                  pipeline or a map reservation.
                </p>
              </section>
            )}
          </>
        ) : (
          <>
            <dl className="ag-summary" aria-label="All agent totals">
              <div>
                <dt>Sales team</dt>
                <dd>{agents.length}</dd>
              </div>
              <div>
                <dt>Open deals</dt>
                <dd>{totals.open}</dd>
              </div>
              <div>
                <dt>Sale value</dt>
                <dd>{money(totals.value / 100)}</dd>
              </div>
              <div>
                <dt>Recorded commission</dt>
                <dd>{money(totals.commission / 100)}</dd>
              </div>
            </dl>
            <p className="ag-scope">
              All-time totals across the directory. Cancelled deals are
              excluded. Recorded commission reflects sale calculations; payouts
              are not tracked here.
            </p>
            <div className="ag-toolbar">
              <label className="ag-search">
                Search agents
                <input
                  type="search"
                  placeholder="Name, email or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <label>
                Role
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="ALL">All roles</option>
                  {Object.entries(roles).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Workload
                <select
                  value={workload}
                  onChange={(e) => setWorkload(e.target.value)}
                >
                  <option value="ALL">All agents</option>
                  <option value="OPEN">With open deals</option>
                  <option value="NONE">No open deals</option>
                </select>
              </label>
              <label>
                Sort by
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="name">Name: A–Z</option>
                  <option value="openDeals">Most open deals</option>
                  <option value="saleValue">Highest sale value</option>
                  <option value="commission">Highest commission</option>
                </select>
              </label>
            </div>
            <div className="ag-results">
              <span>
                {filtered.length} of {agents.length}{" "}
                {agents.length === 1 ? "agent" : "agents"}
              </span>
              {(search || role !== "ALL" || workload !== "ALL") && (
                <button className="ag-link" onClick={resetFilters}>
                  Clear filters
                </button>
              )}
              <span>
                Includes selling roles; administrator accounts are excluded.
              </span>
            </div>
            {filtered.length ? (
              <div className="ag-table-wrap">
                <table className="ag-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th className="number">Clients</th>
                      <th>Deals</th>
                      <th className="number">Sale value</th>
                      <th className="number">Commission</th>
                      <th>
                        <span className="ag-sr">View agent</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <tr key={a.id}>
                        <td data-label="Agent">
                          <div className="ag-person">
                            <span className="ag-avatar" aria-hidden="true">
                              {initials(a.name)}
                            </span>
                            <div>
                              <button
                                className="ag-name"
                                onClick={() => setSelectedId(a.id)}
                              >
                                {a.name}
                              </button>
                              <span>
                                {roles[a.role]} · {a.email}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td data-label="Clients" className="number">
                          {a.assignedClients}
                        </td>
                        <td data-label="Deals">
                          <strong>{a.metrics.openDeals} open</strong>
                          <small>{a.metrics.completedDeals} completed</small>
                        </td>
                        <td data-label="Sale value" className="number">
                          {money(a.metrics.saleValue)}
                        </td>
                        <td data-label="Commission" className="number">
                          {money(a.metrics.commission)}
                        </td>
                        <td className="ag-actions">
                          <button
                            className="ag-link"
                            onClick={() => setSelectedId(a.id)}
                            aria-label={`View ${a.name}`}
                          >
                            View agent
                            <Icon icon={arrowIcon} width={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <section className="ag-empty">
                <h3>
                  {agents.length
                    ? "No agents match these filters"
                    : "Your agent directory is ready"}
                </h3>
                <p>
                  {agents.length
                    ? "Try another name, role or workload."
                    : "Add a sales account to start assigning clients and tracking performance."}
                </p>
                {agents.length ? (
                  <button className="ag-button" onClick={resetFilters}>
                    Clear filters
                  </button>
                ) : (
                  admin && (
                    <button
                      className="ag-button primary"
                      onClick={() => openForm()}
                    >
                      Add your first agent
                    </button>
                  )
                )}
              </section>
            )}
          </>
        )}
      </main>
    </>
  );
}
